import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTank } from '@/contexts/TankContext';
import { journalApi } from '@/api/journal';
import type { JournalEntryCreate, JournalEntryUpdate } from '@/types/journal';

/**
 * Hook to fetch journal entries for the current tank.
 * Supports optional search filter.
 */
export function useJournalEntries(params?: { search?: string }) {
  const { selectedTank } = useTank();

  return useQuery({
    queryKey: ['journal', selectedTank?.id, params],
    queryFn: () => journalApi.list(selectedTank!.id, params),
    enabled: !!selectedTank?.id,
  });
}

/**
 * Hook to create a new journal entry.
 * Invalidates journal queries on success and shows notification.
 */
export function useCreateJournalEntry() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: JournalEntryCreate) => journalApi.create(selectedTank!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journal', selectedTank?.id] });
      notifications.show({
        title: 'Success',
        message: 'Journal entry saved',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to save journal entry',
        color: 'red',
      });
    },
  });
}

/**
 * Hook to update an existing journal entry.
 * Invalidates journal queries on success and shows notification.
 */
export function useUpdateJournalEntry() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: JournalEntryUpdate }) =>
      journalApi.update(selectedTank!.id, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journal', selectedTank?.id] });
      notifications.show({
        title: 'Success',
        message: 'Journal entry updated',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update journal entry',
        color: 'red',
      });
    },
  });
}

/**
 * Hook to toggle pinned status of a journal entry.
 * Invalidates journal queries on success.
 */
export function useToggleJournalPin() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (entryId: string) => journalApi.togglePin(selectedTank!.id, entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journal', selectedTank?.id] });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update pin status',
        color: 'red',
      });
    },
  });
}

/**
 * Hook to delete a journal entry.
 * Invalidates journal queries on success and shows notification.
 */
export function useDeleteJournalEntry() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (entryId: string) => journalApi.delete(selectedTank!.id, entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journal', selectedTank?.id] });
      notifications.show({
        title: 'Success',
        message: 'Journal entry deleted',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete journal entry',
        color: 'red',
      });
    },
  });
}
